import React, { useEffect, useState } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Grid,
  GridItem,
  Image,
  Input,
  HStack,
  Button,
  useToast,
  Text,
  Center,
  Box,
  Tooltip,
  Tag,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { useUploadMedia, useRemoveMedia, useGetCourse } from "../api";
import Loader from "../../../components/Loader";
import { colors } from "../../../theme/tokens/colors";
import { getFileExtension } from "../../../utils/text";
import RiveAnimation from "../../../renderer/elements/Rive";

interface ImageDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  editMode: boolean;
}

const ImageDrawer: React.FC<ImageDrawerProps> = ({
  isOpen,
  onClose,
  editMode,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const toast = useToast();
  const uploadMedia = useUploadMedia();
  const removeMedia = useRemoveMedia();
  const { courseId = "" } = useParams();
  const course = useGetCourse(courseId);

  useEffect(() => {
    if (course.data?.data) {
      setImageUrls(course.data?.data.images);
    }
  }, [course.isSuccess]);

  if (course.isLoading) {
    return <Loader />;
  }

  if (!courseId) {
    // todo: changte erro messae
    return (
      <Box>
        <Text>Course ID not found</Text>
      </Box>
    );
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      setFiles(newFiles);

      const newPreviews = newFiles.map((file) => URL.createObjectURL(file));
      setPreviews(newPreviews);
    }
  };

  const handleUpload = () => {
    if (files.length === 0) {
      toast({
        title: "No files selected",
        status: "warning",
        position: "top",
        isClosable: true,
      });
      return;
    }

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("course", courseId);
    uploadMedia.mutate(formData, {
      onSuccess: (data: any) => {
        setImageUrls((p) => [...new Set([...p, ...data.data])]);
        setPreviews([]);
        setFiles([]);
      },
    });
  };

  const copyToClipboard = (url: string) => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast({
          title: `Copied: ${url}`,
          status: "success",
          position: "top",
          isClosable: true,
        });
      })
      .catch((err) => {
        console.error("Failed to copy URL: ", err);
      });
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent boxShadow={colors.shadow.white} bg={"background"}>
        <DrawerCloseButton />
        <DrawerHeader>Course imags</DrawerHeader>

        <DrawerBody py={"1rem"}>
          {/* Existing Image URLs Grid */}
          <Text mb={2} fontWeight="bold">
            Existing Images
          </Text>
          <Grid templateColumns="repeat(5, 1fr)" gap={4} mb={4}>
            {imageUrls.map((url, index) => (
              <GridItem
                w={"8rem"}
                border={"1px solid"}
                borderRadius={"base"}
                borderColor={"neutral.800"}
                p={"1rem"}
                display={"flex"}
                alignContent={"center"}
                key={index}
                position="relative"
                _hover={{ bg: "whiteAlpha.100" }}
                onClick={() => copyToClipboard(url)}
              >
                <Button
                  isDisabled={removeMedia.isPending}
                  onClick={() => {
                    removeMedia.mutate(
                      { urls: [url], course: courseId },
                      {
                        onSuccess: () => {
                          setImageUrls((p) =>
                            p.filter((imgurl) => imgurl !== url)
                          );
                        },
                      }
                    );
                  }}
                  size="xs"
                  position="absolute"
                  top={0}
                  left={0}
                  variant={"ghost"}
                  colorScheme="red"
                  ml={"auto"}
                >
                  remove
                </Button>
                {/* Tooltip Wrapper */}
                <Tooltip
                  label={
                    getFileExtension(url) === "riv" ? (
                      <Box>
                        <RiveAnimation
                          src={url}
                          autoplay={true}
                          width={"20rem"}
                          height={"20rem"}
                        />
                      </Box>
                    ) : (
                      <Image
                        src={url}
                        width={"20rem"}
                        height={"20rem"}
                        objectFit="contain"
                      />
                    )
                  }
                  hasArrow
                  bg="gray.800"
                  p={2}
                >
                  {getFileExtension(url) === "riv" ? (
                    <Box>
                      <RiveAnimation
                        src={url}
                        autoplay={true}
                        width={"6rem"}
                        height={"6rem"}
                      />
                      <Tag colorScheme="green">{getFileExtension(url)}</Tag>
                    </Box>
                  ) : (
                    <Box>
                      <Image
                        cursor={"pointer"}
                        src={url}
                        position={"relative"}
                        height={"6rem"}
                        objectFit="contain"
                      />
                      <Tag colorScheme="blue">{getFileExtension(url)}</Tag>
                    </Box>
                  )}
                </Tooltip>
              </GridItem>
            ))}
          </Grid>

          {editMode && (
            <>
              {/* File Input */}
              <Input
                type="file"
                multiple
                onChange={handleFileChange}
                accept="image/*,.riv"
                mb={4}
              />
              {/* Image Previews */}
              <Text mb={2} fontWeight="bold">
                Image Previews
              </Text>
              <HStack px={"1rem"} overflowX="auto" width="full" mb={4}>
                {previews.map((preview, index) => (
                  <Image
                    key={index}
                    src={preview}
                    boxSize="100px"
                    objectFit="contain"
                  />
                ))}
              </HStack>
              <Center>
                <Button
                  isLoading={uploadMedia.isPending}
                  variant={"secondary"}
                  onClick={handleUpload}
                >
                  Upload Images
                </Button>
              </Center>
            </>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default ImageDrawer;
